/**
 * Course settings · "Site și leaduri": what the lead form asks for and the
 * brochure popup — both switched without a redeploy.
 */
import React from "react";
import { ToggleLeft, ToggleRight, CheckCircle2, Gift, FileText } from "lucide-react";

export default function SiteSettings({ data, save, canMutate, busy }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="settings-tab-site">
      <LeadFormModeCard data={data} save={save} canMutate={canMutate} busy={busy} />
      <LeadPopupCard data={data} save={save} canMutate={canMutate} busy={busy} />
    </div>
  );
}

/**
 * The brochure popup, on or off.
 *
 * It interrupts a reader mid-page to offer the course brochure by email, and
 * whether that helps depends on what the site is being measured on this month:
 * addresses collected, or enrolments completed. That is a decision the person
 * running the campaign makes on a Tuesday, so it lives here rather than in a
 * deploy.
 */
/**
 * Which lead form the homepage runs.
 *
 * Two variants rather than an edited form, so going back is a click: the
 * brochure form keeps its fields, its wording and its email exactly as they
 * were. The server reads the same setting on every submission.
 */
const LEAD_FORM_OPTIONS = [
  {
    value: "contact",
    title: "Contact",
    body: "Prenume, nume, email și telefon. Textul: „Vrei să afli mai multe detalii? Completează formularul și te contactăm noi.” Omul primește o confirmare scurtă pe email.",
  },
  {
    value: "brochure",
    title: "Broșură",
    body: "Formularul inițial: funcție didactică, specializare, școală și județ. Omul primește pe email broșura și programa cursului.",
  },
];

function LeadFormModeCard({ data, save, canMutate, busy }) {
  const mode = data.lead_form_mode === "brochure" ? "brochure" : "contact";

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-lead-form-mode">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        <FileText size={16} /> Formularul de lead
      </div>
      <div className="grid gap-2" role="radiogroup" aria-label="Tip formular de lead">
        {LEAD_FORM_OPTIONS.map((opt) => {
          const selected = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!canMutate || busy !== null || selected}
              onClick={() => save({ lead_form_mode: opt.value }, "formular lead")}
              className={[
                "text-left rounded-xl border p-4 transition-colors disabled:cursor-default",
                selected
                  ? "border-[#965EA7] bg-[#F0E7F4]/60"
                  : "border-[#E5E7EB] hover:border-[#965EA7]/60 disabled:opacity-60",
              ].join(" ")}
              data-testid={`lead-form-mode-${opt.value}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold text-[#373A45]">{opt.title}</span>
                {selected && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#965EA7]">
                    <CheckCircle2 size={14} /> Activ
                  </span>
                )}
              </div>
              <div className="text-xs text-[#6B7280] mt-1">{opt.body}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LeadPopupCard({ data, save, canMutate, busy }) {
  const enabled = !!data.lead_popup_enabled;
  // The popup offers the brochure. On the contact form there is nothing to
  // offer, so the switch is shown but locked — and the public site keeps the
  // popup off regardless, so a stale "on" here cannot bring it back.
  const brochureForm = data.lead_form_mode === "brochure";

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-lead-popup">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        <Gift size={16} /> Popup broșură
      </div>

      <div className="rounded-xl bg-[#F0E7F4]/40 border border-[#965EA7]/20 p-4 flex items-center justify-between gap-4" data-testid="lead-popup-toggle-row">
        <div>
          <div className="font-semibold text-[#373A45]">
            {enabled ? "Activat" : "Dezactivat"} — popup broșură
          </div>
          <div className="text-xs text-[#6B7280] mt-1 max-w-xl">
            Apare după ce vizitatorul derulează pagina și oferă broșura pe email.
            Când e dezactivat, nu mai apare nimic pe site: nici la derulare, nici
            la 30 de secunde, nici la ieșirea cu mouse-ul. Formularul de lead din
            pagină rămâne neatins.
          </div>
          {!brochureForm && (
            <div className="text-xs text-[#9A6B00] mt-2 max-w-xl" data-testid="lead-popup-locked-hint">
              Disponibil doar pentru formularul cu broșură. Cât timp formularul este
              „Contact”, popup-ul nu apare pe site.
            </div>
          )}
        </div>
        <button
          disabled={!canMutate || busy !== null || !brochureForm}
          onClick={() => save({ lead_popup_enabled: !enabled }, "popup broșură")}
          className="text-[#965EA7] disabled:opacity-40 shrink-0"
          data-testid="settings-lead-popup-toggle"
        >
          {enabled ? <ToggleRight size={40} /> : <ToggleLeft size={40} />}
        </button>
      </div>
    </div>
  );
}
